import { useEffect, useRef } from "react";
import { COLORS } from "../../lib/brand";
import { Animated, Platform, View, type ViewStyle } from "react-native";

const SAND_DARK = "#D9D2C5";
const SAND = COLORS.SAND.hex;

/**
 * Skeleton — soft sand-tinted placeholder block with a slow opacity pulse.
 *
 * Use these instead of <ActivityIndicator/> on first-load. Skeletons match the
 * final layout's shape so the page doesn't reflow when data arrives — that's
 * the perceived-quality win.
 *
 * Props mirror a simple block: width/height/radius. Pass a className if you
 * want to use Tailwind size classes instead.
 */
export function Skeleton({
  width,
  height = 14,
  radius = 4,
  style,
  className,
}: {
  width?: number | string;
  height?: number;
  radius?: number;
  style?: ViewStyle;
  className?: string;
}) {
  const opacity = useRef(new Animated.Value(0.55)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 1100,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(opacity, {
          toValue: 0.55,
          duration: 1100,
          useNativeDriver: Platform.OS !== "web",
        }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [opacity]);

  return (
    <Animated.View
      className={className}
      style={[
        {
          width: (width as unknown as number) ?? "100%",
          height,
          borderRadius: radius,
          backgroundColor: SAND_DARK,
          opacity,
        },
        style,
      ]}
    />
  );
}

/** A taller block for card placeholders. */
export function SkeletonBlock({ height = 120, style }: { height?: number; style?: ViewStyle }) {
  return <Skeleton height={height} radius={6} style={style} />;
}

/**
 * A row of stacked skeleton lines — title + two body lines. Use inside
 * placeholder cards.
 */
export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <View className="gap-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height={i === 0 ? 16 : 11}
          width={i === 0 ? "70%" : i === lines - 1 ? "55%" : "92%"}
        />
      ))}
    </View>
  );
}

/**
 * Card-shaped placeholder with internal padding + a brief stack of text
 * skeletons. Good drop-in for "Active Cases" / "Pre-Release" / case-row lists.
 */
export function SkeletonCard({ height = 92 }: { height?: number }) {
  return (
    <View
      className="rounded-card border bg-white"
      style={{ borderColor: SAND_DARK, padding: 16, minHeight: height, backgroundColor: SAND }}
    >
      <SkeletonText />
    </View>
  );
}
